import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { Loader2, Send, HandHeart } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTEREST_TESTIDS = ["food-pantry", "disaster-relief", "events", "sorting-supplies", "international"];

const inputCls = "w-full rounded-xl bg-white/70 border border-[#E2E8E4] px-4 py-3 text-sm text-[#1A2F24] placeholder:text-[#5C7368]/60 focus:outline-none focus:border-[#F4A261] transition-colors duration-300";

const VolunteerForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(false);
  const interestOptions = t("vol_interests");

  const toggle = (i) => setInterests((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/volunteer`, { ...form, interests });
      toast.success(t("vol_success"));
      setForm({ name: "", email: "", phone: "", message: "" });
      setInterests([]);
    } catch {
      toast.error(t("form_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="volunteer-form" className="rounded-3xl border border-[#E2E8E4] bg-[#F2F4F0] p-8 sm:p-10">
      <HandHeart size={24} className="text-[#F4A261]" />
      <h3 className="font-display text-3xl mt-3 text-[#1A2F24]">{t("vol_title")}</h3>
      <p className="mt-2 text-sm text-[#5C7368]">{t("vol_sub")}</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input required placeholder={t("vol_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="volunteer-name-input" className={inputCls} />
        <input required type="email" placeholder={t("vol_email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="volunteer-email-input" className={inputCls} />
        <input placeholder={t("vol_phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="volunteer-phone-input" className={`${inputCls} sm:col-span-2`} />
      </div>
      <div className="mt-4 flex flex-wrap gap-2" data-testid="volunteer-interests">
        {interestOptions.map((i, idx) => (
          <button type="button" key={i} onClick={() => toggle(i)} data-testid={`volunteer-interest-${INTEREST_TESTIDS[idx]}`}
            className={`rounded-full px-4 py-2 text-xs tracking-wide transition-all duration-300 ${
              interests.includes(i) ? "bg-[#1E352A] text-[#FAF9F6]" : "bg-white/70 border border-[#E2E8E4] text-[#5C7368] hover:border-[#F4A261]"
            }`}>
            {i}
          </button>
        ))}
      </div>
      <textarea rows={3} placeholder={t("vol_note")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="volunteer-message-input" className={`${inputCls} mt-4`} />
      <button disabled={loading} data-testid="volunteer-submit-btn"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1E352A] text-[#FAF9F6] px-8 py-3.5 text-sm tracking-[0.12em] uppercase transition-colors duration-300 hover:bg-[#F4A261] hover:text-white disabled:opacity-60">
        {loading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {t("vol_submit")}
      </button>
    </form>
  );
};

const ContactForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("con_success"));
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(t("form_error"));
    } finally {
      setLoading(false);
    }
  };

  const darkInput = "w-full rounded-xl bg-white/10 border border-white/15 px-4 py-3 text-sm text-[#FAF9F6] placeholder:text-[#C4D7D1]/60 focus:outline-none focus:border-[#F4A261] transition-colors duration-300";

  return (
    <form onSubmit={submit} data-testid="contact-form" id="contact" className="rounded-3xl bg-[#1E352A] p-8 sm:p-10">
      <Send size={24} className="text-[#F4A261]" />
      <h3 className="font-display text-3xl mt-3 text-[#FAF9F6]">{t("con_title")}</h3>
      <p className="mt-2 text-sm text-[#C4D7D1]">{t("con_sub")}</p>
      <div className="mt-6 space-y-4">
        <input required placeholder={t("vol_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" className={darkInput} />
        <input required type="email" placeholder={t("vol_email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" className={darkInput} />
        <textarea required rows={4} placeholder={t("con_msg")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" className={darkInput} />
      </div>
      <button disabled={loading} data-testid="contact-submit-btn"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#F4A261] text-white px-8 py-3.5 text-sm tracking-[0.12em] uppercase font-semibold transition-colors duration-300 hover:bg-[#C4D7D1] hover:text-[#1E352A] disabled:opacity-60">
        {loading ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {t("con_submit")}
      </button>
      <p className="mt-6 text-xs text-[#C4D7D1]/70 leading-relaxed">
        2886 Running Brook Cir, Kissimmee, Florida<br />{t("con_phone_label")}: 407-613-9157
      </p>
    </form>
  );
};

const Forms = () => {
  const { t } = useLanguage();
  return (
    <section id="volunteer" className="py-28 lg:py-36 bg-[#F2F4F0]/50 border-t border-[#E2E8E4]/70" data-testid="forms-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="mb-16">
          <p className="text-xs tracking-[0.3em] uppercase text-[#5C7368] mb-6">{t("forms_kicker")}</p>
          <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight text-[#1A2F24]">{t("forms_title")}</h2>
        </Reveal>
        <div className="grid grid-cols-12 gap-6 lg:gap-8">
          <Reveal className="col-span-12 lg:col-span-7"><VolunteerForm /></Reveal>
          <Reveal delay={0.12} className="col-span-12 lg:col-span-5"><ContactForm /></Reveal>
        </div>
      </div>
    </section>
  );
};

export default Forms;
